/**
 *
 * AudiencesTable
 *
 */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

// styles
import "./style.scss";

// antd component
import { Popconfirm } from "antd";

// assets
import { DeleteOutlined } from "@ant-design/icons";

// components
import CustomTable from "components/CustomTable";
import { NotificationSimple } from "components/Notifications";

function AudiencesTable({
  dataSource,
  isLoading,
  // default
  editable,
  editMode,
  // actions
  onRemoveRow,
  // props
  className,
  ...rest
}) {
  const childClassNames = classNames("audiences-list-table", className);
  const { t } = useTranslation();

  const [showSuccess, setShowSuccess] = useState(false);

  const actionsColumn = {
    title: t("common.action"),
    dataIndex: "action",
    key: "action-column-student",
    className: "column-actions",
    render: (_, record) => {
      return (
        <Popconfirm
          placement="top"
          title={t("common.removalRequest")}
          okText="Oui"
          cancelText="Non"
          onConfirm={() => {
            !!onRemoveRow && onRemoveRow(record);
            setShowSuccess(true);
          }}
          overlayClassName="popconfirm-window audiences-table-popconfirm"
        >
          <DeleteOutlined className="delete-action" />
        </Popconfirm>
      );
    },
  };

  const statusColumn = {
    title: t("common.status"),
    dataIndex: "status",
    key: "column-status",
    className: "column-status",
    render: (_, record) =>
      record?.status?.value && (
        <span className={`table-status ${record?.status?.value}`}>
          {record?.status?.label}
        </span>
      ),
  };

  const columns = [
    {
      title: t("common.lastNameShort"),
      dataIndex: "lastname",
      key: "column-lastname",
      className: "column-lastname",
      sorter: (a, b) => a?.lastname?.localeCompare(b?.lastname),
      render: (text, record) => record?.lastname,
    },
    {
      title: t("common.firstName"),
      dataIndex: "firstname",
      key: "column-firstname",
      className: "column-firstname",
      sorter: (a, b) => a?.firstname?.localeCompare(b?.firstname),
      render: (text, record, index) => record?.firstname,
    },
    {
      title: t("labels.type"),
      dataIndex: "type",
      key: "column-type",
      className: "column-type",
      sorter: (a, b) => a?.type?.localeCompare(b?.type),
      render: (text, record, index) => record?.type,
    },
    {
      title: t("labels.class"),
      dataIndex: "className",
      key: "column-class",
      className: "column-class",
      sorter: (a, b) =>
        a?.class_info?.title?.localeCompare(b?.class_info?.title),
      render: (text, record, index) => {
        return record?.class_info?._id ? (
          <Link
            className="link-table"
            to={`/classes/${record?.class_info?._id}`}
          >
            {record?.class_info?.title}
          </Link>
        ) : (
          ""
        );
      },
    },
  ];

  const [tableColumns, setTableColumns] = useState(columns || []);

  // If mode was changes, change inner mode
  useEffect(() => {
    const cols = tableColumns.filter(
      col => col?.dataIndex != "action" && col?.dataIndex != "status",
    );
    if (!!editable) {
      if (!!editMode) {
        cols.push(actionsColumn);
        setTableColumns(cols);
      } else {
        cols.push(statusColumn);
        setTableColumns(cols);
      }
    } else {
      setTableColumns(cols);
    }
  }, [editable, editMode]);

  return (
    <>
      <NotificationSimple
        //   Loading={isLoading}
        Successful={showSuccess}
        message={t("notifications.during")}
        messageSuccess={t("notifications.memberDeleted")}
        description=""
        type="delete"
      />
      <CustomTable
        className={childClassNames}
        dataSource={dataSource || []}
        columns={tableColumns}
        loading={isLoading}
      />
    </>
  );
}

AudiencesTable.defaultProps = {
  editMode: false,
  dataSource: [],
  isLoading: false,
};
AudiencesTable.propTypes = {
  onRemoveRow: PropTypes.func,
};

export default AudiencesTable;
